import { Injectable, computed, signal } from '@angular/core';
import { ApiService } from './api.service';
import { CartItem } from '../models/cart.model';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class CartService {
  readonly cartItems = signal<CartItem[]>([]);
  readonly loading = signal(false);
  readonly error = signal('');
  /** Total units in cart (sum of quantities), not number of line items. */
  readonly totalQuantity = computed(() =>
    this.cartItems().reduce((sum, item) => sum + (Number(item.CartQty) || 0), 0)
  );

  constructor(private readonly api: ApiService) {}

  loadCart(userId: number): void {
    this.loading.set(true);
    this.error.set('');
    this.api
      .get<unknown[]>(`/Cart/${userId}`)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (items) =>
          this.cartItems.set(Array.isArray(items) ? items.map((item) => this.normalizeCartItem(item)) : []),
        error: () => {
          this.cartItems.set([]);
          this.error.set('Failed to load cart.');
        }
      });
  }

  addToCart(userId: number, bookId: number): Observable<unknown> {
    return this.api.post('/Cart/add', { CartUserId: userId, CartBookId: bookId, CartQty: 1 });
  }

  updateQuantity(cartId: number, qty: number): Observable<unknown> {
    return this.api.put('/Cart/quantity', { CartId: cartId, CartQty: qty });
  }

  remove(cartId: number): Observable<unknown> {
    return this.api.delete(`/Cart/${cartId}`);
  }

  clear(userId: number): Observable<unknown> {
    return this.api.delete(`/Cart/clear/${userId}`);
  }

  private normalizeCartItem(source: any): CartItem {
    return {
      CartId: source.CartId ?? source.cartId ?? 0,
      CartUserId: source.CartUserId ?? source.cartUserId ?? 0,
      CartBookId: source.CartBookId ?? source.cartBookId ?? 0,
      CartQty: source.CartQty ?? source.cartQty ?? 0,
      CartLineTotal: source.CartLineTotal ?? source.cartLineTotal ?? 0,
      BookTitle: source.BookTitle ?? source.bookTitle ?? '',
      BookAuthorName: source.BookAuthorName ?? source.bookAuthorName ?? '',
      BookImageUrl: source.BookImageUrl ?? source.bookImageUrl ?? source.BookImage ?? source.bookImage ?? ''
    };
  }
}
